import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { ApiError, api, type SetupLink, type UserRecord } from '../lib/api';
import { Empty, ErrorNote, PageHeading, SetupLinkPanel, Spinner } from '../components/ui';
import { Confirm } from '../components/Confirm';
import {
  Ledger,
  LedgerToolbar,
  onStage,
  statusFacet,
  searchRows,
  type LedgerColumn,
  type LedgerGroup,
} from '../components/Ledger';
import { useListView } from '../lib/useListView';
import { useIsPlatformAdmin } from '../lib/useSession';

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function columnsFor(actions: {
  /** Both ask first and wait on the promise; Reactivate alone is one click. */
  onLink: (user: UserRecord) => Promise<unknown>;
  onToggle: (user: UserRecord) => Promise<unknown>;
  linking: boolean;
  toggling: boolean;
}): LedgerColumn<UserRecord>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      sort: (user) => user.name,
      search: (user) => user.name,
      className: 'font-medium',
      cell: (user) => (
        <Link
          to="/manage/users/$userId"
          params={{ userId: user.id }}
          className={
            user.isActive ? 'hover:underline' : 'text-ink-faint line-through hover:text-ink'
          }
        >
          {user.name}
        </Link>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      sort: (user) => user.email,
      search: (user) => user.email,
      className: 'font-mono text-xs text-ink-soft',
      cell: (user) => user.email ?? '-',
    },
    {
      id: 'classes',
      header: 'Classes',
      numeric: true,
      sort: (user) => user.classCount,
      className: 'font-mono tabular',
      // A link for the same reason the periods table's is: the number was the
      // whole of the answer, and there was nowhere to go to see which classes.
      cell: (user) =>
        user.classCount === 0 ? (
          <span className="text-ink-soft">none</span>
        ) : (
          <Link to="/manage/users/$userId" params={{ userId: user.id }} className="hover:underline">
            {user.classCount}
          </Link>
        ),
    },
    {
      /*
       * Sorted on the raw value, shown in the reader's own locale. Sorting the
       * rendered string would order it by however the locale spells a month.
       */
      id: 'seen',
      header: 'Last signed in',
      sort: (user) => user.lastLoginAt,
      className: 'font-mono text-xs text-ink-soft',
      cell: (user) =>
        user.pendingSetup
          ? 'setup pending'
          : user.lastLoginAt
            ? new Date(user.lastLoginAt).toLocaleDateString()
            : 'never',
    },
    {
      id: 'actions',
      header: 'Actions',
      headerHidden: true,
      align: 'right',
      // No Manage: the row is the link to the person (see Ledger.tsx).
      cell: (user) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Confirm
            trigger={user.pendingSetup ? 'New link' : 'Reset password'}
            title={`Send ${user.name} a new setup link?`}
            description="Any setup link already issued to them stops working. Their current password keeps working until they use the new one."
            confirmLabel={user.pendingSetup ? 'New link' : 'Reset password'}
            pendingLabel="Creating…"
            disabled={!user.isActive || actions.linking}
            onConfirm={() => actions.onLink(user)}
            errorFallback="Could not create a setup link."
          />
          {user.isActive ? (
            <Confirm
              trigger="Deactivate"
              title={`Deactivate ${user.name}?`}
              description="They can no longer sign in. Anything they marked stays attributed to them."
              confirmLabel="Deactivate"
              pendingLabel="Deactivating…"
              disabled={actions.toggling}
              onConfirm={() => actions.onToggle(user)}
              errorFallback="Could not deactivate that account."
            />
          ) : (
            <button
              type="button"
              className="btn"
              disabled={actions.toggling}
              onClick={() => void actions.onToggle(user).catch(() => {})}
            >
              Reactivate
            </button>
          )}
        </div>
      ),
    },
  ];
}

/*
 * Grouped by role and filtered by whether the account still works, because
 * those are two different questions. Grouping on the one the chips already
 * answer would say it twice.
 */
const BY_ROLE: LedgerGroup<UserRecord> = {
  of: (user) => user.role,
  order: ['org_admin', 'teacher'],
  label: (id, within) => (
    <>
      {id === 'org_admin' ? 'Administrators' : 'Teachers'}
      <span className="font-mono text-xs font-normal text-ink-soft tabular">{within.length}</span>
    </>
  ),
};

export function ManageUsers() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [link, setLink] = useState<SetupLink | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [withPassword, setWithPassword] = useState(false);
  const [role, setRole] = useState<'teacher' | 'org_admin'>('teacher');

  const search = useSearch({ from: '/app/manage/users' });
  const [shut, setShut] = useState<ReadonlySet<string>>(new Set());

  const users = useQuery({ queryKey: ['users'], queryFn: api.admin.users });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const create = useMutation({
    mutationFn: () =>
      api.admin.createUser({
        name,
        email,
        role,
        ...(withPassword ? { password } : {}),
      }),
    onSuccess: async (created) => {
      setName('');
      setEmail('');
      setPassword('');
      setAdding(false);
      if (created.setup) setLink(created.setup);
      await invalidate();
    },
  });

  const sendLink = useMutation({
    mutationFn: (id: string) => api.admin.setupLink(id),
    onSuccess: async (data) => {
      setLink(data);
      await invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.admin.updateUser(id, { isActive }),
    onSuccess: invalidate,
  });

  const columns = useMemo(
    () =>
      columnsFor({
        onLink: (user) => sendLink.mutateAsync(user.id),
        onToggle: (user) => toggle.mutateAsync({ id: user.id, isActive: !user.isActive }),
        linking: sendLink.isPending,
        toggling: toggle.isPending,
      }),
    [sendLink, toggle],
  );
  const view = useListView({ search, columns });
  const show = view.get('show');
  const isPlatformAdmin = useIsPlatformAdmin();

  const all = useMemo(() => users.data?.users ?? [], [users.data]);
  const rows = useMemo(
    () => searchRows(onStage(all, show), columns, view.query),
    [all, columns, show, view.query],
  );

  return (
    <div>
      <PageHeading eyebrow="Who can sign in" title="Staff">
        <button type="button" className="btn btn-primary" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add staff member'}
        </button>
      </PageHeading>

      {link ? (
        <SetupLinkPanel url={link.url} expiresAt={link.expiresAt} onDismiss={() => setLink(null)} />
      ) : null}

      {sendLink.error ? (
        <div className="mb-5">
          <ErrorNote message={message(sendLink.error, 'Could not generate a setup link.')} />
        </div>
      ) : null}
      {toggle.error ? (
        <div className="mb-5">
          <ErrorNote message={message(toggle.error, 'Could not update that account.')} />
        </div>
      ) : null}

      {adding ? (
        <form
          className="panel mb-5 grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          {create.error ? (
            <div className="sm:col-span-2 lg:col-span-3">
              <ErrorNote message={message(create.error, 'Could not add staff member.')} />
            </div>
          ) : null}
          <label className="grid gap-1.5">
            <span className="eyebrow">Name</span>
            <input
              className="field"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Email</span>
            <input
              className="field"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Role</span>
            <select
              className="field"
              value={role}
              onChange={(e) => setRole(e.target.value as 'teacher' | 'org_admin')}
            >
              <option value="teacher">Teacher - only assigned classes</option>
              <option value="org_admin">Administrator - everything</option>
            </select>
          </label>

          <fieldset className="grid gap-1.5 sm:col-span-2 lg:col-span-3">
            <legend className="eyebrow mb-1.5">How they get in</legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="credential"
                checked={!withPassword}
                onChange={() => setWithPassword(false)}
              />
              Send a setup link - they choose their own password
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="credential"
                checked={withPassword}
                onChange={() => setWithPassword(true)}
              />
              Set a temporary password now
            </label>
            {withPassword ? (
              <input
                className="field mt-1.5 font-mono sm:max-w-sm"
                type="text"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="at least 12 characters"
              />
            ) : null}
          </fieldset>

          <div className="sm:col-span-2 lg:col-span-3">
            <button className="btn btn-primary" type="submit" disabled={create.isPending}>
              {create.isPending ? 'Adding…' : 'Add staff member'}
            </button>
          </div>
        </form>
      ) : null}

      {users.isLoading ? (
        <Spinner />
      ) : (
        <>
          <LedgerToolbar
            facets={[
              statusFacet({
                label: 'Filter staff by status',
                liveLabel: 'Active',
                inactiveLabel: 'Deactivated',
                value: show,
                onChange: (next) => view.set({ show: next || undefined }),
                live: onStage(all, '').length,
                inactive: onStage(all, 'inactive').length,
                deleted: isPlatformAdmin ? onStage(all, 'deleted').length : undefined,
              }),
            ]}
            query={view.draft}
            onQuery={view.onQuery}
            searchPlaceholder="name or email"
            shown={rows.length}
            total={all.length}
          />
          <Ledger
            caption="Staff"
            rows={rows}
            total={all.length}
            columns={columns}
            rowId={(user) => user.id}
            sort={view.sort}
            onSort={view.onSort}
            /*
             * Unconditional, unlike the four lists that group on the very thing
             * their filter controls. Role and whether the account works are two
             * different questions, so the headings stay and the filter simply
             * takes rows out of them.
             */
            group={BY_ROLE}
            collapsed={shut}
            onCollapse={(id, closed) =>
              setShut((prev) => {
                const next = new Set(prev);
                if (closed) next.add(id);
                else next.delete(id);
                return next;
              })
            }
            empty={<Empty title="No staff yet" hint="Add the teachers who will take attendance." />}
            noMatch={
              <Empty
                title="Nobody to show"
                hint={
                  view.query
                    ? 'Nobody here matches that name or email.'
                    : show === 'deleted'
                      ? 'Nothing has been deleted here.'
                      : show === 'inactive'
                        ? 'No account here is deactivated.'
                        : 'Every account here is deactivated. Switch to Deactivated to see them.'
                }
              />
            }
          />
        </>
      )}
    </div>
  );
}
